<template>
  <div class="thread">
    <div v-if="loading && !conversation" class="text-center text-muted py-5 my-auto">
      <span class="spinner-border spinner-border-sm mr-1"></span> Loading…
    </div>
    <div v-else-if="error" class="text-center text-muted py-5 px-3 my-auto">
      <i class="fe fe-lock" style="font-size: 1.8rem; opacity: 0.4"></i>
      <p class="mt-2 mb-0">{{ error }}</p>
    </div>

    <template v-else>
      <!-- Header -->
      <div class="thead">
        <button type="button" class="thead-back d-md-none" title="Back" @click="$emit('back')">
          <i class="fe fe-arrow-left"></i>
        </button>
        <ChatAvatar :name="title" :id="avatarId" :group="isGroup" :size="40" />
        <div class="thead-main">
          <div class="thead-name text-truncate">{{ title }}</div>
          <small class="thead-sub text-truncate d-block">{{ subtitle }}</small>
        </div>
        <button
          v-if="conversation && conversation.can_add_participants"
          type="button"
          class="thead-action"
          title="Add people"
          @click="adding = true"
        >
          <i class="fe fe-user-plus"></i>
        </button>
      </div>

      <!-- Messages -->
      <div ref="scroller" class="tbody">
        <div v-if="hasMore" class="text-center mb-2">
          <button type="button" class="btn btn-sm btn-light" :disabled="loadingOlder" @click="loadOlder">
            {{ loadingOlder ? "Loading…" : "Earlier messages" }}
          </button>
        </div>
        <p v-if="!conversation" class="tempty">
          Say hello — your chat with {{ title }} starts with your first message.
        </p>

        <template v-for="(m, i) in messages">
          <div v-if="newDay(i)" :key="'d' + m.id" class="tday"><span>{{ day(m.created_at) }}</span></div>
          <div :key="m.id" class="mrow" :class="{ 'mrow--mine': mine(m), 'mrow--cont': sameSender(i) }">
            <div class="bubble" :class="{ 'bubble--mine': mine(m), 'bubble--deleted': m.deleted }">
              <div v-if="isGroup && !mine(m) && !sameSender(i)" class="bubble-sender" :style="{ color: senderColor(m.sender.id) }">
                {{ m.sender.name }}
              </div>
              <em v-if="m.deleted">This message was deleted</em>
              <template v-else>
                <span v-if="text(m)" class="bubble-text">{{ text(m) }}</span>
                <div v-if="m.tags.length" class="bubble-tagrow">
                  <!-- One record: its type icon, opens it directly. Several: tag icon + count, opens the list. -->
                  <button
                    v-if="m.tags.length === 1"
                    type="button"
                    class="bubble-tagicon"
                    :title="'Open ' + m.tags[0].label"
                    @click="openOne(m.tags[0])"
                  >
                    <i class="fe fe-file-text"></i>
                  </button>
                  <button
                    v-else
                    type="button"
                    class="bubble-tagicon"
                    :title="m.tags.map((t) => t.label).join(', ')"
                    @click="showTags = m.tags"
                  >
                    <i class="fe fe-file-text"></i>
                    <span class="bubble-tagcount">{{ m.tags.length }}</span>
                  </button>
                </div>
              </template>
              <span class="bubble-meta">
                <button v-if="m.can_delete" type="button" class="bubble-del" title="Delete message" @click="remove(m)">
                  <i class="fe fe-trash-2"></i>
                </button>
                {{ clock(m.created_at) }}
              </span>
            </div>
          </div>
        </template>
      </div>

      <!-- Input -->
      <div class="tfoot">
        <ComposeBox ref="compose" :types="types" :sending="sending" @submit="send" />
      </div>
    </template>

    <TagsModal v-if="showTags" :tags="showTags" @close="showTags = null" />
    <AddPeopleModal v-if="adding" :conversation="conversation" @close="adding = false" @saved="onPeopleAdded" />
  </div>
</template>

<script>
import api, { errorMessage } from "../api";
import chat from "../store";
import ChatAvatar from "./ChatAvatar.vue";
import ComposeBox from "./ComposeBox.vue";
import TagsModal from "./TagsModal.vue";
import { openTag } from "../openTag";
import AddPeopleModal from "./AddPeopleModal.vue";
import { POLL_THREAD_MS, storedUser } from "../config";
import { clock, conversationTitle, dayLabel, otherPeople, stripTokens } from "../format";

const SENDER_COLORS = ["#2563eb", "#0891b2", "#059669", "#7c3aed", "#db2777", "#ea580c"];
const TYPE_NAMES = { shis: "Agency", employee: "Agency staff", provider: "Facility", provider_employee: "Facility staff", tpa: "HMO", tpa_employee: "HMO staff" };

// One chat. With `conversationId` it shows an existing chat; with `draft`
// ({to, cc}: user summaries) it is a new chat created by the first message.
export default {
  name: "ConversationThread",
  components: { ChatAvatar, ComposeBox, TagsModal, AddPeopleModal },
  props: {
    conversationId: { type: [Number, String], default: null },
    draft: { type: Object, default: null },
    types: { type: Array, default: () => [] },
  },
  data() {
    return {
      conversation: null,
      messages: [], // oldest first
      hasMore: false,
      nextBeforeId: null,
      loading: false,
      loadingOlder: false,
      sending: false,
      adding: false,
      showTags: null,
      error: "",
      myId: Number((storedUser() || {}).id),
      unwatch: null,
      poll: null,
    };
  },
  computed: {
    people() {
      if (this.conversation) return otherPeople(this.conversation, this.myId);
      if (!this.draft) return [];
      return this.draft.to.map((u) => ({ user_id: u.id, role: "to", ...u }))
        .concat(this.draft.cc.map((u) => ({ user_id: u.id, role: "cc", ...u })));
    },
    isGroup() {
      return this.people.length > 1;
    },
    title() {
      if (this.conversation) return conversationTitle(this.conversation, this.myId);
      return this.people.map((p) => p.name).join(", ");
    },
    avatarId() {
      return this.people[0] ? this.people[0].user_id : 0;
    },
    subtitle() {
      if (this.isGroup) {
        return "You, " + this.people.map((p) => p.name + (p.role === "cc" ? " (cc)" : "")).join(", ");
      }
      const p = this.people[0];
      return p ? [p.job_title || TYPE_NAMES[p.type], p.organisation].filter(Boolean).join(" · ") : "";
    },
    lastId() {
      return this.messages.length ? this.messages[this.messages.length - 1].id : 0;
    },
  },
  watch: {
    conversationId: {
      immediate: true,
      handler() {
        if (this.conversationId) this.open();
      },
    },
  },
  created() {
    document.addEventListener("visibilitychange", this.onVisible);
    this.poll = setInterval(() => {
      if (!chat.state.connected && !document.hidden && this.conversation) this.loadNewer();
    }, POLL_THREAD_MS);
  },
  mounted() {
    if (this.draft) this.$nextTick(() => this.$refs.compose && this.$refs.compose.focus());
  },
  beforeDestroy() {
    this.close();
    document.removeEventListener("visibilitychange", this.onVisible);
    clearInterval(this.poll);
  },
  methods: {
    clock,
    day: dayLabel,
    text: (m) => stripTokens(m.body),
    openOne(tag) {
      openTag(this, tag);
    },
    mine(m) {
      return Number(m.sender.id) === this.myId;
    },
    newDay(i) {
      return i === 0 || this.messages[i - 1].created_at.slice(0, 10) !== this.messages[i].created_at.slice(0, 10);
    },
    sameSender(i) {
      return i > 0 && !this.newDay(i) && this.messages[i - 1].sender.id === this.messages[i].sender.id;
    },
    senderColor(id) {
      return SENDER_COLORS[Math.abs(Number(id)) % SENDER_COLORS.length];
    },
    open() {
      this.close();
      const id = Number(this.conversationId);
      chat.state.activeConversationId = id;
      Object.assign(this, { conversation: null, messages: [], hasMore: false, nextBeforeId: null, error: "", loading: true });

      Promise.all([api.conversation(id), api.messages(id)])
        .then(([conversation, page]) => {
          if (Number(this.conversationId) !== id) return;
          this.conversation = conversation;
          this.messages = page.data.slice().reverse();
          this.hasMore = page.has_more;
          this.nextBeforeId = page.next_before_id;
          this.scrollToBottom();
          this.markRead();
          this.unwatch = chat.watchConversation(id, {
            created: () => this.loadNewer(),
            deleted: (p) => this.markDeleted(p.message_id),
          });
          this.$nextTick(() => this.$refs.compose && this.$refs.compose.focus());
        })
        .catch((error) => {
          this.error = error.response && error.response.status === 403
            ? "You are not part of this chat."
            : "Couldn't load this chat.";
        })
        .finally(() => {
          this.loading = false;
        });
    },
    close() {
      if (this.unwatch) this.unwatch();
      this.unwatch = null;
      if (this.conversationId && Number(chat.state.activeConversationId) === Number(this.conversationId)) {
        chat.state.activeConversationId = null;
      }
    },
    loadOlder() {
      if (!this.nextBeforeId) return;
      this.loadingOlder = true;
      const el = this.$refs.scroller;
      const fromBottom = el ? el.scrollHeight - el.scrollTop : 0;
      api
        .messages(this.conversation.id, this.nextBeforeId)
        .then((page) => {
          this.messages = page.data.slice().reverse().concat(this.messages);
          this.hasMore = page.has_more;
          this.nextBeforeId = page.next_before_id;
          this.$nextTick(() => {
            if (el) el.scrollTop = el.scrollHeight - fromBottom;
          });
        })
        .finally(() => {
          this.loadingOlder = false;
        });
    },
    loadNewer() {
      if (!this.conversation) return;
      api.messages(this.conversation.id).then((page) => {
        page.data.forEach((m) => {
          const shown = this.messages.find((x) => x.id === m.id);
          if (shown && m.deleted && !shown.deleted) Object.assign(shown, m);
        });
        const fresh = page.data.filter((m) => m.id > this.lastId).reverse();
        if (!fresh.length) return;
        const atBottom = this.isAtBottom();
        this.messages = this.messages.concat(fresh);
        if (atBottom || fresh.some(this.mine)) this.scrollToBottom();
        this.markRead();
      });
    },
    send({ body, tags }) {
      this.sending = true;
      const request = this.conversation
        ? api.reply(this.conversation.id, { body, tags }).then((message) => {
            if (!this.messages.some((m) => m.id === message.id)) this.messages.push(message);
            this.scrollToBottom();
            this.$emit("changed");
          })
        : api
            .createConversation({ to: this.draft.to.map((u) => u.id), cc: this.draft.cc.map((u) => u.id), body, tags })
            .then((res) => this.$emit("created", res.conversation));

      request
        .then(() => this.$refs.compose && this.$refs.compose.reset())
        .catch((error) => this.$toasted.error(errorMessage(error), { position: "top-center", duration: 4000 }))
        .finally(() => {
          this.sending = false;
          this.$nextTick(() => this.$refs.compose && this.$refs.compose.focus());
        });
    },
    /** Opening a chat (or receiving a message while it is open) marks it read. */
    markRead() {
      if (document.hidden || !this.conversation || !this.lastId) return;
      api
        .markRead(this.conversation.id, this.lastId)
        .then((res) => {
          chat.setUnread(res.unread_count);
          chat.bus.$emit("read", res);
        })
        .catch(() => {});
    },
    onVisible() {
      if (document.hidden) return;
      // Polling pauses in hidden tabs: catch up now, then mark read.
      if (!chat.state.connected && this.conversation) this.loadNewer();
      else this.markRead();
    },
    remove(message) {
      if (!confirm("Delete this message for everyone?")) return;
      api
        .deleteMessage(message.id)
        .then(() => {
          this.markDeleted(message.id);
          this.$emit("changed");
        })
        .catch((error) => this.$toasted.error(errorMessage(error), { position: "top-center", duration: 4000 }));
    },
    markDeleted(messageId) {
      const m = this.messages.find((x) => x.id === Number(messageId));
      if (m) Object.assign(m, { deleted: true, body: null, tags: [], can_delete: false });
    },
    onPeopleAdded(conversation) {
      this.conversation = conversation;
      this.adding = false;
      this.$toasted.info("People added", { position: "top-center", duration: 2500 });
      this.$emit("changed");
    },
    isAtBottom() {
      const el = this.$refs.scroller;
      return !el || el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.scroller;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
  },
};
</script>

<style scoped>
.thread {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
}
.thead {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-bottom: 1px solid #e9ecef;
  background: #fff;
}
.thead-main {
  flex: 1;
  min-width: 0;
}
.thead-name {
  font-weight: 700;
  color: #111827;
}
.thead-sub {
  color: #64748b;
}
.thead-back,
.thead-action {
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 0;
  background: transparent;
  color: #475569;
  font-size: 1.1rem;
}
.thead-back:hover,
.thead-action:hover {
  background: #f1f5f9;
}
.tbody {
  flex: 1;
  overflow-y: auto;
  padding: 16px 5%;
  background: #eef2f7;
}
.tempty {
  text-align: center;
  color: #64748b;
  font-size: 0.88rem;
  margin: 24px auto;
  max-width: 320px;
}
.tday {
  text-align: center;
  margin: 14px 0 10px;
}
.tday span {
  background: #fff;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 8px;
  box-shadow: 0 1px 1px rgba(15, 23, 42, 0.06);
}
.mrow {
  display: flex;
  margin-top: 8px;
}
.mrow--cont {
  margin-top: 2px;
}
.mrow--mine {
  justify-content: flex-end;
}
.bubble {
  position: relative;
  max-width: 72%;
  background: #fff;
  border-radius: 12px;
  border-top-left-radius: 4px;
  padding: 6px 10px 18px;
  min-width: 90px;
  box-shadow: 0 1px 1px rgba(15, 23, 42, 0.08);
  font-size: 0.92rem;
  color: #111827;
}
.bubble--mine {
  background: #dbeafe;
  border-top-left-radius: 12px;
  border-top-right-radius: 4px;
}
.mrow--cont .bubble {
  border-radius: 12px;
}
.bubble--deleted {
  color: #94a3b8;
}
.bubble-sender {
  font-size: 0.78rem;
  font-weight: 700;
  margin-bottom: 2px;
}
.bubble-text {
  display: block;
  white-space: pre-wrap;
  overflow-wrap: break-word;
}
.bubble-tagrow {
  margin-top: 4px;
}
.bubble-tagicon {
  position: relative;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 0;
  background: #eaf1fe;
  color: #1d4ed8;
  font-size: 0.95rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.bubble-tagicon:hover {
  background: #d5e3fc;
}
.bubble-tagcount {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 16px;
  height: 16px;
  border-radius: 8px;
  background: #2563eb;
  color: #fff;
  font-size: 0.62rem;
  font-weight: 700;
  line-height: 16px;
  padding: 0 4px;
}
.bubble-meta {
  position: absolute;
  right: 10px;
  bottom: 3px;
  font-size: 0.68rem;
  color: #64748b;
}
.bubble-del {
  border: 0;
  background: transparent;
  color: #94a3b8;
  padding: 0 4px 0 0;
  font-size: 0.72rem;
  visibility: hidden;
}
.bubble:hover .bubble-del {
  visibility: visible;
}
.bubble-del:hover {
  color: #dc2626;
}
.tfoot {
  padding: 10px 16px;
  border-top: 1px solid #e9ecef;
  background: #f8fafc;
}
@media (max-width: 767.98px) {
  .tbody {
    padding: 12px;
  }
  .bubble {
    max-width: 85%;
  }
}
</style>
